import React, { useMemo, useState } from "react";
import ClassRow from "./ClassRow";
import Icon from "./Icon";
import { countCourses, filterCourses } from "./classesModel";
import "./myClasses.css";

const FILTERS = [["all", "All"], ["live", "Live"], ["upcoming", "Upcoming"], ["draft", "Draft"]];
const SKELETON_ROWS = 4;

function EmptyState({ pastTab }) {
  return (
    <div className="empty">
      <Icon n="school" s={36} />
      <h3>{pastTab ? "No completed classes yet" : "No courses assigned"}</h3>
      <p>
        {pastTab
          ? "Classes you taught will appear here after a coordinator marks them complete."
          : "Contact your Course Coordinator to be assigned to a course."}
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="list" aria-busy="true" aria-label="Loading classes">
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} className="skrow">
          <span className="sk" style={{ width: 28 }} />
          <span className="sk" style={{ width: 180 + (i % 3) * 50 }} />
          <span className="sk" style={{ width: 200 }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Instructor "My classes" list: page head, Current/Past tabs, status filter and search, and one expandable row per
 * class. `actions` supplies the navigation handlers; `actions.onAddSession` is omitted where sessions can't be requested.
 */
export default function MyClassesView({ courses, sessionsMap, requestsMap, listTab, onListTab, loading, error, actions, now = new Date() }) {
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const past = listTab === "past";

  const counts = useMemo(() => countCourses(courses, sessionsMap), [courses, sessionsMap]);
  const visible = useMemo(
    () => filterCourses(courses, sessionsMap, { status: past ? "all" : status, query }),
    [courses, sessionsMap, status, query, past],
  );
  const today = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const switchTab = (tab) => { setStatus("all"); onListTab(tab); };

  return (
    <div className="mc-root">
      <p className="eyebrow">Instructor dashboard</p>
      <h1>My classes</h1>
      <p className="sub">
        {today} · {counts.live > 0
          ? <>You are leading <b>{counts.live} live session{counts.live === 1 ? "" : "s"}</b> right now</>
          : "No live sessions right now"}
      </p>

      <nav className="tabs" aria-label="Class period">
        {[["current", "Current"], ["past", "Past"]].map(([key, label]) => (
          <button key={key} type="button" className={listTab === key ? "on" : ""} aria-current={listTab === key ? "page" : undefined} onClick={() => switchTab(key)}>
            {label}
          </button>
        ))}
      </nav>

      {error && <div className="warn" role="alert">{error}</div>}

      {loading ? <Skeleton /> : courses.length === 0 ? <EmptyState pastTab={past} /> : (
        <>
          <div className="head">
            <h2>{past ? "Past classes" : "All assigned classes"}<span>{visible.length} total</span></h2>
            <div className="tools">
              {!past && (
                <div className="seg" role="group" aria-label="Filter classes">
                  {FILTERS.map(([key, label]) => (
                    <button key={key} type="button" className={status === key ? "on" : ""} aria-pressed={status === key}
                      disabled={key !== "all" && counts[key] === 0} onClick={() => setStatus(key)}>
                      {label} <b>{counts[key]}</b>
                    </button>
                  ))}
                </div>
              )}
              <label className="search">
                <Icon n="search" s={15} />
                <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search classes, codes, locations" aria-label="Search classes" />
              </label>
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="list"><div className="none">No classes match{query.trim() ? ` “${query.trim()}”` : " your filters"}.</div></div>
          ) : (
            <div className="list">
              {visible.map((course) => (
                <ClassRow key={course.id} course={course} sessions={sessionsMap[course.id] || []} now={now}
                  pendingRequests={(requestsMap[course.id] || []).filter((r) => r.status === "pending").length}
                  actions={actions} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
